/**
 * Landing Page V2 - Guided Activities Section
 */

'use client';

import React from 'react';
import { Card } from '@/design-system';
import {
  typographyColors,
  backgroundColors,
  spacing,
  typography,
  fontPrimary,
  borders,
} from '@/design-system/utils';
import Image from 'next/image';
import Link from 'next/link';
import { useGuidedActivities } from '@/hooks/useExperiences';
import type { Experience } from '@/services/experienceService';
import { getExperienceImage } from '@/services/experienceService';
import { getCategoryMeta } from '@/constants/experienceCategoryMeta';
import { ScrollReveal } from './ScrollReveal';
import { useCurrency } from '@/hooks/useCurrency';
import { useConvertedPrice } from '@/hooks/useCurrencyPrice';
import { getCurrencyCode } from '@/utils/currencyHelpers';

// Fallback activities if API fails (not used - sections hide if no API data)
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const fallbackActivities: Experience[] = [
  {
    _id: 'act-1',
    name: 'Ranthambore Safari',
    title:
      'Ranthambore Safari & Wildlife Experience – Tigers, Forts & Lakeside Magic',
    slug: 'ranthambore-safari',
    code: 'ACT-001',
    type: 'activity',
    category: {
      title: 'Adventure',
      description: 'Wildlife safari',
      _id: 'cat-act-1',
    },
    location: {
      _id: 'loc-act-1',
      name: 'Ranthambore',
      state: 'Rajasthan',
      city: 'Sawai Madhopur',
    },
    imageUrl: ['/assets/images/activity/activity-1.png'],
    price: 4000,
    currencySymbol: '₹',
    averageRating: 4.7,
    reviewCount: 95,
    isActive: true,
    curated: true,
    createdAt: new Date().toISOString(),
  },
  {
    _id: 'act-2',
    name: 'Pondicherry Coastal',
    title:
      'Pondicherry Coastal Escape – French Quarters, Beaches & Bohemian Bliss',
    slug: 'pondicherry-coastal',
    code: 'ACT-002',
    type: 'activity',
    category: {
      title: 'Group',
      description: 'Group activity',
      _id: 'cat-act-2',
    },
    location: {
      _id: 'loc-act-2',
      name: 'Pondicherry',
      state: 'Puducherry',
      city: 'Pondicherry',
    },
    imageUrl: ['/assets/images/activity/activity-2.png'],
    price: 2000,
    currencySymbol: '₹',
    averageRating: 4.6,
    reviewCount: 87,
    isActive: true,
    curated: true,
    createdAt: new Date().toISOString(),
  },
  {
    _id: 'act-3',
    name: 'Varanasi Spiritual',
    title:
      'Varanasi Spiritual Voyage – Ghats, Temples & The Eternal Flow of the Ganges',
    slug: 'varanasi-spiritual',
    code: 'ACT-003',
    type: 'activity',
    category: {
      title: 'Group',
      description: 'Spiritual tour',
      _id: 'cat-act-3',
    },
    location: {
      _id: 'loc-act-3',
      name: 'Varanasi',
      state: 'Uttar Pradesh',
      city: 'Varanasi',
    },
    imageUrl: ['/assets/images/activity/activity-3.png'],
    price: 3000,
    currencySymbol: '₹',
    averageRating: 4.9,
    reviewCount: 112,
    isActive: true,
    curated: true,
    createdAt: new Date().toISOString(),
  },
];

export function GuidedActivitiesSection() {
  // Fetch guided activities using React Query
  const { data, isLoading, error } = useGuidedActivities();

  // Ref for scrollable container
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const isDraggingRef = React.useRef(false);
  const startXRef = React.useRef(0);
  const scrollLeftRef = React.useRef(0);
  const hasDraggedRef = React.useRef(false);
  const shouldPreventClickRef = React.useRef(false);

  // Debug logging
  React.useEffect(() => {
    console.log('=== GuidedActivities Debug ===');
    console.log('isLoading:', isLoading);
    console.log('data:', data);
    console.log('data length:', data?.length);
    console.log('error:', error);
    console.log('=============================');
  }, [data, isLoading, error]);

  // Only use API data, don't show fallback
  // Hide section if no API data
  const hasApiData = data && Array.isArray(data) && data.length > 0;
  const activities = hasApiData ? data : [];

  // Enable mouse drag scrolling
  React.useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || isLoading) return;

    const handleMouseDown = (e: MouseEvent) => {
      // Check if container has horizontal overflow
      const hasHorizontalScroll = container.scrollWidth > container.clientWidth;
      if (!hasHorizontalScroll) {
        return;
      }

      // Allow dragging even on links, but we'll prevent navigation if dragged
      isDraggingRef.current = true;
      hasDraggedRef.current = false;
      const rect = container.getBoundingClientRect();
      startXRef.current = e.clientX - rect.left;
      scrollLeftRef.current = container.scrollLeft;

      container.style.cursor = 'grabbing';
      container.style.userSelect = 'none';

      // Prevent default to avoid text selection and link navigation
      e.preventDefault();
      e.stopPropagation();
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDraggingRef.current) return;

      e.preventDefault();
      e.stopPropagation();

      const rect = container.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const walk = x - startXRef.current; // Distance moved

      // Only mark as dragged if movement is significant (more than 5px)
      if (Math.abs(walk) > 5) {
        hasDraggedRef.current = true;
        shouldPreventClickRef.current = true;
      }

      // Update scroll position
      const newScrollLeft = scrollLeftRef.current - walk;
      container.scrollLeft = newScrollLeft;
    };

    const handleMouseUp = () => {
      isDraggingRef.current = false;

      if (container) {
        container.style.cursor = 'grab';
        container.style.userSelect = '';
      }

      // Reset hasDragged after a short delay to allow click handler to check it
      if (hasDraggedRef.current) {
        setTimeout(() => {
          hasDraggedRef.current = false;
          shouldPreventClickRef.current = false;
        }, 200);
      } else {
        shouldPreventClickRef.current = false;
        hasDraggedRef.current = false;
      }
    };

    const handleMouseLeave = () => {
      isDraggingRef.current = false;
      hasDraggedRef.current = false;
      shouldPreventClickRef.current = false;
      if (container) {
        container.style.cursor = 'grab';
        container.style.userSelect = '';
      }
    };

    const handleClick = (e: MouseEvent) => {
      // Prevent navigation if we dragged
      if (shouldPreventClickRef.current || hasDraggedRef.current) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        return false;
      }
    };

    container.addEventListener('mousedown', handleMouseDown, {
      passive: false,
    });
    document.addEventListener('mousemove', handleMouseMove, { passive: false });
    document.addEventListener('mouseup', handleMouseUp);
    container.addEventListener('mouseleave', handleMouseLeave);
    // Use capture phase to intercept clicks before they reach links
    container.addEventListener('click', handleClick, { capture: true });

    return () => {
      container.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      container.removeEventListener('mouseleave', handleMouseLeave);
      container.removeEventListener('click', handleClick, { capture: true });
    };
  }, [isLoading, activities.length]);

  console.log('GuidedActivities - Final activities count:', activities.length);
  console.log(
    'GuidedActivities - Using:',
    data && data.length > 0 ? 'API DATA' : 'FALLBACK'
  );

  // Hide section if no API data
  if (!hasApiData && !isLoading) {
    return null;
  }

  return (
    <ScrollReveal
      as='section'
      className='
    pt-4 pb-8 md:pt-14 md:pb-14
    px-4 md:pl-[80px] md:pr-0
  '
      style={{
        background:
          'linear-gradient(to bottom, #ffffff 0%, #fbf9f4 48.077%, #ffffff 95.192%)',
      }}
      direction='up'
      amount={0.25}
      margin='0px 0px -120px 0px'
    >
      <ScrollReveal
        className='flex flex-col md:flex-row items-center w-full gap-8 md:gap-16'
        delay={0.1}
        direction='none'
      >
        {/* Left Content */}
        <ScrollReveal
          className='w-full md:w-[430px] flex flex-col gap-4 md:gap-16'
          delay={0.2}
          direction='left'
        >
          <div className='flex flex-col gap-2 md:gap-4 text-center md:text-left'>
            <div
              className='flex items-center justify-center md:justify-start shrink-0 rounded-[8px]'
              style={{
                gap: spacing[2.5], // 10px
                paddingLeft: spacing[1], // 4px
                paddingRight: spacing[3], // 12px
                paddingTop: spacing[1], // 4px
                paddingBottom: spacing[1], // 4px
              }}
            >
              <div
                className='flex items-center justify-center shrink-0 border border-black/12'
                style={{
                  backgroundColor: '#fde047', // Yellow-300 from Figma
                  padding: spacing[2], // 8px
                  borderRadius: borders.radius.full,
                  width: '36px',
                  height: '36px',
                }}
              >
                <Image
                  src='/assets/images/icons/landing-page/guided_activities.svg'
                  alt='Guided Activities'
                  width={20}
                  height={20}
                  unoptimized
                />
              </div>
              <p
                className='shrink-0'
                style={{
                  fontFamily: fontPrimary,
                  fontWeight: typography.weight.bold,
                  fontSize: typography.size.base, // 16px
                  lineHeight: 'normal',
                  color: typographyColors.primary,
                }}
              >
                Guided Activities
              </p>
            </div>
            <h2
              className='font-semibold text-[24px] md:text-[48px] md:leading-[52px]'
              style={{
                fontFamily: fontPrimary,
                fontWeight: typography.weight.semibold,
                lineHeight: 'normal',
                color: typographyColors.primary,
              }}
            >
              Handpicked activities at every destination
            </h2>

            <p
              className='font-normal text-black text-sm md:text-xl'
              style={{
                fontFamily: fontPrimary,
                fontWeight: typography.weight.regular,
                lineHeight: 'normal',
                color: typographyColors.primary,
              }}
            >
              Discover What to Do
            </p>
          </div>

          {/* Desktop only - Explore link in left section */}
          <div
            className='hidden md:flex justify-start items-center'
            style={{ gap: spacing[1.5] }}
          >
            <Link
              href='/experiences?type=activities'
              className='cursor-pointer hover:opacity-80 transition-opacity'
              style={{
                fontFamily: fontPrimary,
                fontWeight: 700,
                fontSize: '16px',
                lineHeight: 'normal',
                color: typographyColors.primary,
                textUnderlinePosition: 'from-font',
                textDecorationLine: 'underline',
                textDecorationStyle: 'solid',
              }}
            >
              Explore All Activities
            </Link>
            <svg
              width='20'
              height='20'
              viewBox='0 0 20 20'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                fillRule='evenodd'
                d='M5.22 14.78a.75.75 0 001.06 0l7.22-7.22v5.69a.75.75 0 001.5 0v-7.5a.75.75 0 00-.75-.75h-7.5a.75.75 0 000 1.5h5.69l-7.22 7.22a.75.75 0 000 1.06z'
                clipRule='evenodd'
                fill='#27272a'
              />
            </svg>
          </div>
        </ScrollReveal>

        {/* Right Content - Activity Cards */}
        <div
          className='flex-1 w-full -mx-4 md:mx-0 md:px-0 md:w-[857px]'
          style={{
            paddingTop: spacing[14],
            overflow: 'hidden',
          }}
        >
          <div
            ref={scrollContainerRef}
            className='flex flex-nowrap overflow-x-auto overflow-y-clip relative scrollbar-hide h-[383px] md:h-[453px] gap-8'
            style={{
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              WebkitOverflowScrolling: 'touch',
              scrollBehavior: 'auto',
              overflowX: 'auto',
              overflowY: 'hidden',
              cursor: 'grab',
              display: 'flex',
              flexDirection: 'row',
              width: '100%',
              paddingLeft: spacing[4], // 16px on mobile
              paddingRight: spacing[4], // 16px on mobile
            }}
          >
            {isLoading ? (
              // Loading skeleton
              <>
                {[1, 2, 3].map(i => (
                  <div
                    key={i}
                    className='flex-shrink-0 animate-pulse rounded-lg overflow-hidden'
                    style={{
                      width: '286px',
                      height: '453px',
                      backgroundColor: backgroundColors.secondary,
                      minWidth: '286px',
                    }}
                  />
                ))}
              </>
            ) : (
              activities.map((activity, index) => (
                <ScrollReveal
                  key={activity._id}
                  className='flex-shrink-0'
                  delay={0.35 + index * 0.06}
                  direction='up'
                  amount={0.15}
                >
                  <ActivityCard experience={activity} />
                </ScrollReveal>
              ))
            )}
          </div>

          {/* Mobile only - Explore link below cards */}
          <div
            className='flex md:hidden justify-center items-center mt-6'
            style={{ gap: spacing[1.5] }}
          >
            <Link
              href='/experiences?type=activities'
              className='cursor-pointer hover:opacity-80 transition-opacity'
              style={{
                fontFamily: fontPrimary,
                fontWeight: 700,
                fontSize: '16px',
                lineHeight: 'normal',
                color: typographyColors.primary,
                textUnderlinePosition: 'from-font',
                textDecorationLine: 'underline',
                textDecorationStyle: 'solid',
              }}
            >
              Explore All Activities
            </Link>
            <svg
              width='20'
              height='20'
              viewBox='0 0 20 20'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                fillRule='evenodd'
                d='M5.22 14.78a.75.75 0 001.06 0l7.22-7.22v5.69a.75.75 0 001.5 0v-7.5a.75.75 0 00-.75-.75h-7.5a.75.75 0 000 1.5h5.69l-7.22 7.22a.75.75 0 000 1.06z'
                clipRule='evenodd'
                fill='#27272a'
              />
            </svg>
          </div>
        </div>
      </ScrollReveal>
    </ScrollReveal>
  );
}

interface ActivityCardProps {
  experience: Experience;
}

function ActivityCard({ experience }: ActivityCardProps) {
  const {
    title,
    location,
    shortTitle,
    name,
    category,
    price,
    currencySymbol,
    type,
    attractions,
    slug,
  } = experience;

  // Get image from media array, with fallback to imageUrl
  const displayImage = getExperienceImage(experience);

  // Use location name
  const displayLocation = location
    ? location.city || location.name
    : 'Location TBD';

  // Use shortTitle if available, otherwise title or name
  const displayTitle = shortTitle || title || name;

  const categoryMeta = getCategoryMeta(type, category?.title);

  // Infer backend currency from currencySymbol (₹ = INR, $ = USD, etc.)
  // Defaults to USD if currencySymbol doesn't match known symbols
  const getBackendCurrency = (symbol?: string | null): string => {
    if (!symbol) return 'USD';
    if (symbol === '₹' || symbol === 'INR') return 'INR';
    if (symbol === '$' || symbol === 'USD') return 'USD';
    // Default to USD for unknown symbols
    return 'USD';
  };

  const backendCurrency = getBackendCurrency(currencySymbol);
  const { currencySymbol: userCurrencySymbol, currencyCode } = useCurrency();
  const backendCurrencyCode = getCurrencyCode(backendCurrency);
  const convertedAmount = useConvertedPrice(price || 0, backendCurrencyCode);

  // Smart conversion: only use converted value if currencies don't match
  const finalAmount =
    backendCurrencyCode !== currencyCode ? convertedAmount : price || 0;

  // Format price with currency symbol and thousand separators
  const displayPriceAmount = price
    ? Math.round(finalAmount).toLocaleString()
    : '';

  const cardWidthMobile = '260px';

  return (
    <Link href={`/activity-details/${slug}`}>
      <Card
        padding='none'
        className='relative w-[260px] md:w-[286px] h-full overflow-hidden group cursor-pointer flex-shrink-0 hover:opacity-90 transition-opacity rounded-xl'
        style={{ minWidth: cardWidthMobile, borderRadius: borders.radius.xl }}
      >
        {/* Background Image */}
        <Image
          src={displayImage}
          alt={displayTitle}
          fill
          className='object-cover rounded-xl'
          style={{ borderRadius: borders.radius.xl }}
        />

        {/* Gradient Overlay - Matching Figma exactly */}
        <div
          className='absolute inset-0 rounded-xl'
          style={{
            background:
              'linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.5) 55.769%, rgba(0,0,0,0.8) 100%)',
            borderRadius: borders.radius.xl,
          }}
        />

        {/* Content */}
        <div
          className='relative h-full flex flex-col justify-between rounded-lg'
          style={{
            paddingTop: spacing[4], // 16px
            paddingBottom: spacing[4], // 16px
            paddingLeft: spacing[4], // 16px
            paddingRight: spacing[4], // 16px
            borderRadius: borders.radius.lg,
          }}
        >
          {/* Guided Activities Badge - Matching Figma Design */}
          <div className='flex justify-start w-full'>
            <GuidedActivitiesBadge
              attractionsCount={attractions?.length || 0}
              categoryLabel={categoryMeta.label}
            />
          </div>

          {/* Bottom Content */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: spacing[4], // 16px gap from Figma
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: spacing[1], // 4px gap per Figma
              }}
            >
              <h3
                className='line-clamp-3'
                style={{
                  fontFamily: fontPrimary,
                  fontWeight: typography.weight.bold,
                  fontSize: '24px', // 24px from Figma
                  lineHeight: 'normal',
                  color: typographyColors['alt-offset'],
                  margin: 0,
                }}
              >
                {displayTitle}
              </h3>
              <p
                style={{
                  fontFamily: fontPrimary,
                  fontWeight: typography.weight.light,
                  fontSize: '16px', // 16px from Figma
                  lineHeight: 'normal',
                  color: typographyColors['alt-offset'],
                  margin: 0,
                }}
              >
                {displayLocation}
              </p>
            </div>

            {/* Divider */}
            <div
              style={{
                height: '1px',
                background: 'rgba(255, 255, 255, 0.80)',
              }}
            />

            {/* Price Container */}
            {price && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0,
                  }}
                >
                  {/* Currency Symbol - Dynamically updates between ₹ (INR) and $ (USD) */}
                  <div
                    className='flex items-center justify-center shrink-0'
                    style={{
                      width: '20px',
                      height: '20px',
                      color: 'white',
                      fontFamily: fontPrimary,
                      fontSize: '16px',
                      lineHeight: 'normal',
                    }}
                  >
                    {userCurrencySymbol || '₹'}
                  </div>

                  {/* Price Text */}
                  <div
                    className='flex flex-col justify-center whitespace-nowrap'
                    style={{
                      fontFamily: fontPrimary,
                      lineHeight: '0',
                    }}
                  >
                    <p style={{ lineHeight: 'normal' }}>
                      <span
                        style={{
                          fontWeight: 500,
                          fontSize: '16px',
                          color: 'white',
                        }}
                      >
                        {displayPriceAmount}
                      </span>
                      <span
                        style={{
                          fontWeight: 500,
                          fontSize: '16px',
                          color: 'white',
                        }}
                      >
                        {' '}
                      </span>
                      <span
                        style={{
                          fontWeight: 500,
                          fontSize: '16px',
                          color: 'white',
                        }}
                      >
                        /
                      </span>
                      <span
                        style={{
                          fontWeight: 300,
                          fontSize: '10px',
                          color: 'white',
                        }}
                      >
                        Person
                      </span>
                    </p>
                  </div>
                </div>
                <svg
                  width='20'
                  height='20'
                  viewBox='0 0 20 20'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    fillRule='evenodd'
                    d='M5.22 14.78a.75.75 0 001.06 0l7.22-7.22v5.69a.75.75 0 001.5 0v-7.5a.75.75 0 00-.75-.75h-7.5a.75.75 0 000 1.5h5.69l-7.22 7.22a.75.75 0 000 1.06z'
                    clipRule='evenodd'
                    fill='white'
                  />
                </svg>
              </div>
            )}
          </div>
        </div>
      </Card>
    </Link>
  );
}

interface GuidedActivitiesBadgeProps {
  attractionsCount: number;
  categoryLabel: string;
  className?: string;
}

function GuidedActivitiesBadge({
  attractionsCount,
  categoryLabel,
  className = '',
}: GuidedActivitiesBadgeProps) {
  return (
    <div
      className={className}
      style={{
        background: 'rgba(255, 255, 255, 0.10)',
        border: '1px solid rgba(255, 255, 255, 0.20)', // More visible border
        display: 'flex',
        padding: spacing[1], // 4px
        justifyContent: 'center',
        alignItems: 'center',
        gap: spacing[1], // 4px
        borderTopLeftRadius: borders.radius.sm, // 4px
        borderTopRightRadius: borders.radius.sm, // 4px
        borderBottomLeftRadius: borders.radius.sm, // 4px
        borderBottomRightRadius: borders.radius.sm, // 4px
        boxShadow: '4px 4px 4px 0 rgba(0, 0, 0, 0.10)',
      }}
    >
      {/* Number Icon Container */}
      <div
        style={{
          backgroundColor: 'rgba(241, 241, 239, 0.19)',
          border: '1px solid rgba(255, 255, 255, 0.15)', // Add border for visibility
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: borders.radius.sm,
          width: '26px',
          height: '26px',
          minWidth: '26px',
          minHeight: '26px',
          flexShrink: 0,
          padding: spacing[1],
        }}
      >
        <p
          style={{
            fontFamily: 'var(--font-primary, sans-serif)',
            fontWeight: 700,
            fontSize: '14px',
            lineHeight: 'normal',
            color: '#FFFFFF',
            margin: 0,
            textShadow: '0px 1px 1px rgba(0, 0, 0, 0.3)',
          }}
        >
          {attractionsCount}
        </p>
      </div>

      {/* Attractions Text */}
      <p
        style={{
          fontFamily: 'var(--font-primary, sans-serif)',
          fontWeight: 400,
          fontSize: '11px',
          lineHeight: 'normal',
          color: '#FFFFFF',
          margin: 0,
          whiteSpace: 'nowrap',
          textShadow: '0px 1px 1px rgba(0, 0, 0, 0.3)',
        }}
      >
        Attractions
      </p>

      {/* Dash Separator */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '12px',
          height: '0px',
          flexShrink: 0,
        }}
        className='flex-none rotate-180'
      >
        <svg
          width='12'
          height='1'
          viewBox='0 0 12 1'
          fill='none'
          style={{ transform: 'rotate(180deg)' }}
        >
          <line
            x1='0'
            y1='0.5'
            x2='12'
            y2='0.5'
            stroke='#FFFFFF'
            strokeWidth='1'
            strokeOpacity='0.5'
          />
        </svg>
      </div>

      {/* Category Pill */}
      <div
        style={{
          backgroundColor: 'rgba(0, 0, 0, 0.19)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: borders.radius.full,
          paddingLeft: spacing[2],
          paddingRight: spacing[2],
          paddingTop: spacing[1],
          paddingBottom: spacing[1],
          flexShrink: 0,
        }}
      >
        <p
          style={{
            fontFamily: 'var(--font-primary, sans-serif)',
            fontWeight: 600,
            fontSize: '9px',
            lineHeight: 'normal',
            color: '#FFFFFF',
            margin: 0,
            whiteSpace: 'nowrap',
          }}
        >
          {categoryLabel}
        </p>
      </div>
    </div>
  );
}
